import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Alert, Container, Row, Col } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';
import { format } from 'date-fns';
import { RiFilter3Fill } from 'react-icons/ri';

import { getTransactionsRest } from '@/services/transactions/transactions-service';
import DebouncedInput from '@/components/DebounceInput';
import StandardLayout from '@/common/layouts/StandardLayout';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';
import CopyField from '@/common/forms/CopyField';
import { config } from '@repo/common-types';
import { TransactionDto } from '@repo/common-types';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import { TransactionFilterDto } from '@/services/transactions/transactions-types';

import { transactionsQueryReducer, initialState, TransactionsActionType } from './transactions-query-reducer';
import TransactionsFilter from './transactions-filter';
import TransactionsTable from '../common/TransactionsTable';
import { getStatusBadgeColor } from '../deposits/utils/get-status-badge-color';
import { formatCurrency } from '@/common/utils/formatCurrency';
import ROUTES from '@/routes/routes-enums';
import { trimHash } from '@/common/utils/trim-hash';

const Transactions: React.FC = () => {
  const navigate = useNavigate();
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [queryParams, dispatch] = useReducer(transactionsQueryReducer, initialState);
  const [searchParams, setSearchParams] = useSearchParams();

  const { sorting, filter, take, skip } = queryParams;

  useEffect(() => {
    if (Object.values(filter).some(Boolean)) {
      const newSearchParams = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value) {
          newSearchParams.set(key, value.toString());
        }
      });
      setSearchParams(newSearchParams);
    } else {
      setSearchParams({});
    }
  }, [filter]);

  useEffect(() => {
    const newFilters: TransactionFilterDto = {};
    searchParams.forEach((value, key) => {
      if (value) {
        newFilters[key as keyof TransactionFilterDto] = value as any;
      }
    });
    if (Object.keys(newFilters).length > 0) {
      dispatch({ type: TransactionsActionType.SET_FILTERS, payload: newFilters });
    }
  }, []);

  const {
    data,
    isLoading,
    error,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: ['transactionData', queryParams],
    queryFn: () =>
      getTransactionsRest({
        filter,
        sorts: convertSortingToApiFormat(sorting),
        take,
        skip,
      }),
    staleTime: 60000,
  });

  const columns: ColumnDef<TransactionDto>[] = useMemo(
    () => [
      {
        accessorKey: 'txId',
        header: 'Transaction ID',
        sortUndefined: 'last',
        cell: ({ row }) => {
          const cellData = row.original.txId;
          const displayAddress = cellData ? trimHash(cellData) : '';
          return (
            <a
              href={`${config.deposits.polygon.blockExplorerUrl}/tx/${cellData}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-underline cursor-pointer"
            >
              {displayAddress}
            </a>
          );
        },
        size: 200,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => {
          const transaction = row.original;

          // Check if it's a tournament-related transaction
          const tournamentTypes = [
            'TOURNAMENT_REGISTRATION',
            'TOURNAMENT_UNREGISTRATION',
            'TOURNAMENT_PRIZE',
            'TOURNAMENT_ELIMINATE_PLAYER',
            'TOURNAMENT_REFUND',
            'TOURNAMENT_KO_PAYOUT',
            'TOURNAMENT_REBUY',
            'TOURNAMENT_ADDON',
            'TOURNAMENT_CLOSE_REGISTRATION',
            'TOURNAMENT_COLLECT_RAKE',
            'TOURNAMENT_DEPLOY',
            'TOURNAMENT_END',
            'TOURNAMENT_EXTRACT_FUNDS',
            'TOURNAMENT_FUND_PRIZE_POOL',
            'TOURNAMENT_PAUSE',
            'TOURNAMENT_START',
            'TOURNAMENT_UNPAUSE',
          ];

          if (tournamentTypes.includes(transaction.type) && transaction.tournamentId) {
            // Display tournament name if available, otherwise show tournament ID
            const displayName = transaction.tournamentName || `Tournament ${transaction.tournamentId.slice(0, 8)}...`;
            return (
              <Link
                to={`${ROUTES.TOURNAMENT_DETAILS}/${transaction.tournamentId}`}
                className="text-decoration-underline text-muted cursor-pointer"
              >
                {displayName}
              </Link>
            );
          }

          // Display table name for table transactions
          if (transaction.table) {
            return (
              <Link
                className="text-decoration-underline cursor-pointer"
                to={`${ROUTES.TABLE}/${transaction.table?.id}`}
              >
                <span>{transaction.table.name}</span>
                <p className="small mb-0">{transaction.table.variants[0]?.replace(/([a-z])([A-Z])/g, '$1 $2')}</p>
              </Link>
            );
          }

          return null;
        },
      },
      {
        accessorKey: 'userAlias',
        header: 'User Alias',
        cell: ({ row }) => (
          <Link to={`${ROUTES.USER_EDIT}/${row.original.userId}`} className="text-decoration-underline cursor-pointer">
            {row.original.userAlias}
          </Link>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        cell: ({ row }) => (
          <span className={`badge ${getStatusBadgeColor(row.original.status)}`}>{row.original.status}</span>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        enableSorting: true,
      },
      {
        accessorKey: 'message',
        header: 'Message',
        cell: ({ row }) => (
          <div
            className="text-muted"
            style={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {row.original.message || '-'}
          </div>
        ),
        enableSorting: false,
        size: 320,
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) =>
          row.original.amount && <div className="text-center">{formatCurrency(row.original.amount, 2)}</div>,
        enableSorting: true,
        meta: {
          className: 'justify-content-center',
        },
      },
      {
        accessorKey: 'updatedAt',
        header: 'Date',
        cell: (info) => format(info.getValue() as Date, 'dd MMM yyyy HH:mm'),
        sortDescFirst: false,
        enableSorting: true,
      },
    ],
    [],
  );

  const handleSearch = useCallback((value: string | number) => {
    setIsDebouncing(true);
    dispatch({ type: TransactionsActionType.SET_SEARCH, payload: value.toString() });
    setIsDebouncing(false);
  }, []);

  const handlePageChange = useCallback((skip: number, take: number) => {
    dispatch({ type: TransactionsActionType.SET_PAGINATION, payload: { skip, take } });
  }, []);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(queryParams.sorting) : updater;
      dispatch({ type: TransactionsActionType.SET_SORTING, payload: newSorting });
    },
    [queryParams.sorting],
  );

  const handleDateChange = useCallback((dates: Date[]) => {
    if (dates.length === 2) {
      const [fromDate, toDate] = dates.map((date) => date.toISOString());
      dispatch({
        type: TransactionsActionType.SET_DATE_RANGE,
        payload: { fromDate: new Date(fromDate), toDate: new Date(toDate) },
      });
    }
  }, []);

  if (error) {
    return (
      <StandardLayout pageTitle="Transactions">
        <Container fluid>
          <Alert color="danger">Error when fetching transaction data</Alert>
        </Container>
      </StandardLayout>
    );
  }

  return (
    <StandardLayout pageTitle="Transactions">
      <Container fluid>
        <Row className="gy-2 mb-2">
          <div className="d-flex justify-content-between mb-3 flex-row gap-3">
            <div className="d-flex flex-rows gap-3">
              <DebouncedInput
                type="text"
                className="form-control"
                placeholder="Search transactions..."
                onChange={handleSearch}
                value={filter.search || ''}
                isLoading={isDebouncing}
              />
              <Button
                onClick={() => setShowFilterModal(true)}
                className="btn btn-outline d-flex align-items-center justify-content-center h-auto gap-2"
                variant={ButtonVariants.secondary}
                size={ButtonSizes.sm}
              >
                <RiFilter3Fill size={20} />
                Filter
              </Button>
              {Object.values(queryParams.filter || {}).some(Boolean) && (
                <Button
                  size={ButtonSizes.sm}
                  variant={ButtonVariants.primary}
                  onClick={() => dispatch({ type: TransactionsActionType.RESET_FILTERS })}
                  className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {showFilterModal && (
              <TransactionsFilter
                onClose={() => setShowFilterModal(false)}
                onApplyFilters={(filters) => {
                  console.log('on apply filters', filters);
                  dispatch({ type: TransactionsActionType.SET_FILTERS, payload: filters });
                  setShowFilterModal(false);
                }}
                currentFilters={queryParams.filter}
              />
            )}

            <div className="d-flex">
              <div className="input-group">
                <Flatpickr
                  className="form-control dash-filter-time-picker border-0 shadow"
                  options={{
                    enableTime: true,
                    dateFormat: 'Y-m-d H:i',
                    mode: 'range',
                    defaultDate: [filter.fromDate, filter.toDate].filter(Boolean) as Date[],
                  }}
                  onChange={handleDateChange}
                />
                <div className="input-group-text bg-primary border-primary text-white">
                  <i className="ri-calendar-2-line"></i>
                </div>
              </div>
            </div>
          </div>

          <TransactionsTable
            transactions={data?.transactions || []}
            refreshTable={refetchTransactions}
            totalCount={data?.totalCount || 0}
            onPageChange={handlePageChange}
            take={take}
            skip={skip}
            onSortingChange={handleSortingChange}
            columns={columns}
            sorting={sorting}
            isLoadingData={isLoading}
          />
        </Row>
      </Container>
    </StandardLayout>
  );
};

export default Transactions;
